/**
 * Admin System Settings.
 */
import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';

const SystemSettings = () => {
  const [settings, setSettings] = useState({
    platform_name: 'CDCS Kenya',
    default_consultation_fee: 500,
    max_queue_size: 20,
    appointment_duration_minutes: 15,
    emergency_priority_enabled: true,
    sms_notifications_enabled: true,
    mpesa_enabled: true,
    maintenance_mode: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await adminAPI.getSettings();
        if (res.data) setSettings(s => ({ ...s, ...(res.data.settings || res.data) }));
      } catch {
        // Use defaults
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSettings(settings);
      toast.success('Settings saved successfully.');
    } catch {
      toast.error('Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const ToggleSwitch = ({ label, description, value, onChange }) => (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="text-sm font-medium text-gray-900">{label}</p>
        {description && <p className="text-xs text-gray-500">{description}</p>}
      </div>
      <button
        onClick={() => onChange(!value)}
        className={`w-11 h-6 rounded-full transition-colors relative ${value ? 'bg-accent-500' : 'bg-gray-300'}`}
      >
        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
          value ? 'translate-x-5' : 'translate-x-0.5'
        }`} />
      </button>
    </div>
  );

  return (
    <Layout title="Settings" role="admin" showBack>
      <div className="p-4 pb-24 space-y-4">
        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="skeleton h-16 rounded-2xl" />)}
          </div>
        ) : (
          <>
            <div className="card space-y-4">
              <h3 className="font-semibold text-gray-900">Platform Configuration</h3>
              <div>
                <label className="label">Platform Name</label>
                <input
                  type="text"
                  value={settings.platform_name}
                  onChange={e => setSettings(s => ({ ...s, platform_name: e.target.value }))}
                  className="input-field"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Default Fee (KES)</label>
                  <input
                    type="number"
                    value={settings.default_consultation_fee}
                    onChange={e => setSettings(s => ({ ...s, default_consultation_fee: parseInt(e.target.value) || 0 }))}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="label">Max Queue Size</label>
                  <input
                    type="number"
                    value={settings.max_queue_size}
                    onChange={e => setSettings(s => ({ ...s, max_queue_size: parseInt(e.target.value) || 20 }))}
                    className="input-field"
                  />
                </div>
              </div>
              <div>
                <label className="label">Appointment Duration (minutes)</label>
                <input
                  type="number"
                  value={settings.appointment_duration_minutes}
                  onChange={e => setSettings(s => ({ ...s, appointment_duration_minutes: parseInt(e.target.value) || 15 }))}
                  className="input-field"
                />
              </div>
            </div>

            <div className="card divide-y divide-gray-100">
              <h3 className="font-semibold text-gray-900 pb-3">Feature Toggles</h3>
              <ToggleSwitch
                label="Emergency Priority"
                description="Enable emergency case prioritization in queue"
                value={settings.emergency_priority_enabled}
                onChange={v => setSettings(s => ({ ...s, emergency_priority_enabled: v }))}
              />
              <ToggleSwitch
                label="SMS Notifications"
                description="Send SMS via Twilio for appointments & prescriptions"
                value={settings.sms_notifications_enabled}
                onChange={v => setSettings(s => ({ ...s, sms_notifications_enabled: v }))}
              />
              <ToggleSwitch
                label="M-Pesa Payments"
                description="Enable M-Pesa payment processing"
                value={settings.mpesa_enabled}
                onChange={v => setSettings(s => ({ ...s, mpesa_enabled: v }))}
              />
              <ToggleSwitch
                label="Maintenance Mode"
                description="Show maintenance page to all non-admin users"
                value={settings.maintenance_mode}
                onChange={v => setSettings(s => ({ ...s, maintenance_mode: v }))}
              />
            </div>

            <button onClick={handleSave} disabled={saving} className="w-full py-3 rounded-xl font-semibold text-white bg-gray-800 hover:bg-gray-900 transition-colors flex items-center justify-center gap-2">
              {saving && <div className="spinner w-5 h-5 border-2"></div>}
              Save Settings
            </button>
          </>
        )}
      </div>
    </Layout>
  );
};

export default SystemSettings;
